import { useState, useEffect, useRef } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X, Download, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackPageView, trackCTAClick } from "@/lib/analytics";
import ConnectWithUs from "@/components/ConnectWithUs";

const LOGO_URL = "/logo.png";
const CATALOGUE_URL = "/Prithira-Global-Catalogue-2026.pdf";

const aboutSubLinks = [
  { name: "Our Story", path: "/about" },
  { name: "Our Credentials", path: "/credentials" },
];

const navLinks = [
  { name: "About", path: "/about", children: aboutSubLinks },
  { name: "Products", path: "/products" },
  { name: "Industries", path: "/industries" },
  { name: "Global Export", path: "/export" },
  { name: "Team", path: "/team" },
  { name: "Blog", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

const AboutDropdown = ({ link, isActive }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <Link
        to={link.path}
        data-testid={`nav-${link.name.toLowerCase()}`}
        className={`inline-flex items-center gap-1 text-xs xl:text-sm font-medium tracking-wide transition-colors hover:text-primary whitespace-nowrap ${
          isActive ? "text-primary" : "text-stone-600"
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        onFocus={handleEnter}
        onBlur={handleLeave}
      >
        {link.name}
        <ChevronDown
          className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </Link>
      {open && (
        <div
          data-testid="nav-about-dropdown"
          className="absolute left-0 top-full pt-3 min-w-[200px] z-50"
        >
          <div className="bg-white/95 backdrop-blur-lg border border-stone-100 rounded-2xl shadow-lg py-2 overflow-hidden">
            {link.children.map((sub) => (
              <Link
                key={sub.path}
                to={sub.path}
                data-testid={`nav-about-${sub.name.toLowerCase().replace(/\s/g, "-")}`}
                className="block px-4 py-2.5 text-sm font-medium text-stone-700 hover:text-primary hover:bg-primary/5 transition-colors"
                onClick={() => setOpen(false)}
              >
                {sub.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/80 backdrop-blur-lg shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-24 md:h-32 lg:h-40">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 md:gap-4 flex-shrink-0"
          >
            <img
              src={LOGO_URL}
              alt="Prithira Global"
              className="h-16 md:h-24 lg:h-32 w-auto"
            />
            <div className="flex flex-col">
              <span className="font-heading text-base md:text-xl lg:text-2xl text-stone-900 font-medium tracking-tight uppercase whitespace-nowrap">
                Prithira Global
              </span>
              <span className="text-[10px] md:text-xs lg:text-sm text-stone-600 font-light italic whitespace-nowrap">
                From Indian Soil to Global Tables
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {navLinks.map((link) =>
              link.children ? (
                <AboutDropdown
                  key={link.path}
                  link={link}
                  isActive={link.children.some(
                    (s) => location.pathname === s.path
                  )}
                />
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-xs xl:text-sm font-medium tracking-wide transition-colors hover:text-primary whitespace-nowrap ${
                    location.pathname === link.path
                      ? "text-primary"
                      : "text-stone-600"
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
            <a
              href={CATALOGUE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-catalogue-btn"
              onClick={() => trackCTAClick("get_catalogue", "navbar")}
            >
              <Button
                variant="outline"
                className="rounded-full px-3 xl:px-5 py-4 xl:py-5 text-xs xl:text-sm font-medium tracking-wide border-2 border-primary text-primary hover:bg-primary/5"
              >
                <Download className="mr-1 xl:mr-2 h-3 w-3 xl:h-4 xl:w-4" />
                Catalogue
              </Button>
            </a>
            <Link to="/partner">
              <Button
                data-testid="nav-partner-btn"
                className="rounded-full px-4 xl:px-6 py-4 xl:py-5 text-xs xl:text-sm font-medium tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 btn-lift whitespace-nowrap"
              >
                Partner With Us
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-stone-700" />
            ) : (
              <Menu className="h-6 w-6 text-stone-700" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden bg-white/95 backdrop-blur-lg border-t border-stone-100 py-6 animate-fade-in"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.path} className="flex flex-col">
                    <span className="text-base font-medium px-4 py-2 text-stone-900 tracking-wide">
                      {link.name}
                    </span>
                    {link.children.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        data-testid={`mobile-nav-${sub.name.toLowerCase().replace(/\s/g, "-")}`}
                        className={`text-sm font-medium pl-8 pr-4 py-2 rounded-lg transition-colors ${
                          location.pathname === sub.path
                            ? "text-primary bg-primary/5"
                            : "text-stone-600 hover:text-primary hover:bg-stone-50"
                        }`}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                    className={`text-base font-medium px-4 py-2 rounded-lg transition-colors ${
                      location.pathname === link.path
                        ? "text-primary bg-primary/5"
                        : "text-stone-600 hover:text-primary hover:bg-stone-50"
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              )}
              <a
                href={CATALOGUE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 mt-2"
                onClick={() => trackCTAClick("get_catalogue", "mobile_menu")}
              >
                <Button
                  variant="outline"
                  className="w-full rounded-full py-5 text-sm font-medium tracking-wide border-2 border-primary text-primary"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Get Catalogue
                </Button>
              </a>
              <Link to="/partner" className="px-4" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full rounded-full py-5 text-sm font-medium tracking-wide bg-primary text-primary-foreground">
                  Partner With Us
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-stone-900 text-white">
      <div className="container-custom py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="bg-white p-2 rounded-lg">
                <img
                  src={LOGO_URL}
                  alt="Prithira Global"
                  className="h-14 w-auto"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-heading text-xl text-white font-medium uppercase">
                  Prithira Global
                </span>
                <span className="text-xs text-stone-400 font-light italic">
                  From Indian Soil to Global Tables
                </span>
              </div>
            </Link>
            <p className="text-stone-400 font-light text-sm leading-relaxed mb-4">
              From Indian Soil to Global Tables
            </p>
            <p className="text-stone-500 text-xs font-light">
              Sustainable biodegradable packaging solutions for responsible businesses worldwide.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/about"
                  className="text-stone-400 text-sm hover:text-white transition-colors"
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  to="/products"
                  className="text-stone-400 text-sm hover:text-white transition-colors"
                >
                  Products
                </Link>
              </li>
              <li>
                <Link
                  to="/industries"
                  className="text-stone-400 text-sm hover:text-white transition-colors"
                >
                  Industries
                </Link>
              </li>
              <li>
                <Link
                  to="/export"
                  className="text-stone-400 text-sm hover:text-white transition-colors"
                >
                  Global Export
                </Link>
              </li>
              <li>
                <Link
                  to="/credentials"
                  data-testid="footer-credentials-link"
                  className="text-stone-400 text-sm hover:text-white transition-colors"
                >
                  Our Credentials
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-heading text-lg mb-6">Our Products</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/products"
                  className="text-stone-400 text-sm hover:text-white transition-colors"
                >
                  Bagasse Tableware
                </Link>
              </li>
              <li>
                <Link
                  to="/products"
                  className="text-stone-400 text-sm hover:text-white transition-colors"
                >
                  Areca Leaf Products
                </Link>
              </li>
              <li>
                <Link
                  to="/products"
                  className="text-stone-400 text-sm hover:text-white transition-colors"
                >
                  Biodegradable Bags
                </Link>
              </li>
              <li>
                <Link
                  to="/products"
                  className="text-stone-400 text-sm hover:text-white transition-colors"
                >
                  Coconut Scrubbers
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg mb-6">Get In Touch</h4>
            <ul className="space-y-3 text-stone-400 text-sm">
              <li>info@prithiraglobal.com</li>
              <li>+91 9830195737</li>
              <li>+91 9674010032</li>
              <li className="pt-2">
                <span className="text-stone-500 text-xs">Headquarters</span>
                <br />
                100/12, Jessore Road, Bhagabati Park,
                <br />
                Dumdum, Kolkata 700074,
                <br />
                West Bengal, India
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-stone-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-stone-500 text-xs">
            &copy; {new Date().getFullYear()} Prithira Global. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/contact"
              className="text-stone-500 text-xs hover:text-white transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/contact"
              className="text-stone-500 text-xs hover:text-white transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to="/admin"
              rel="nofollow"
              data-testid="footer-staff-login-link"
              className="text-stone-500 text-xs hover:text-white transition-colors"
            >
              Staff login
            </Link>
          </div>
        </div>

        <p
          data-testid="footer-iec"
          className="text-stone-500 text-[11px] text-center mt-6 font-light tracking-wide"
        >
          IEC No: AQAPR7398L | Issued by DGFT, Ministry of Commerce &amp; Industry, Government of India
        </p>
      </div>
    </footer>
  );
};

const Layout = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <ConnectWithUs />
      <Footer />
    </div>
  );
};

export default Layout;
